import React from "react";
import { Banknote } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SectionToggle, NumInput } from "./shared";

const DEFAULT_GUARDRAILS = { initial_pct: 4.0, upper_band: 1.2, lower_band: 0.8, raise_pct: 10, cut_pct: 10 };

const STRATEGY_LABEL = {
  fixed_real: "Fixed real $",
  percentage: "% of portfolio",
  guardrails: "Guardrails",
};

export default function StrategySection({ inputs, setInputs, open, onToggle }) {
  const setStrategy = (s) => {
    const patch = { ...inputs, withdrawal_strategy: s };
    if (s === "guardrails" && !inputs.guardrails) patch.guardrails = { ...DEFAULT_GUARDRAILS };
    setInputs(patch);
  };

  const updateGuardrail = (k, v) => {
    setInputs({
      ...inputs,
      guardrails: { ...(inputs.guardrails || DEFAULT_GUARDRAILS), [k]: v },
    });
  };

  const strategyLabel = STRATEGY_LABEL[inputs.withdrawal_strategy || "fixed_real"];

  return (
    <div>
      <SectionToggle
        testId="toggle-strategy"
        icon={Banknote}
        title="Withdrawal strategy"
        subtitle="How you draw down in retirement"
        badge={strategyLabel}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="pb-5 space-y-4">
          <Select value={inputs.withdrawal_strategy || "fixed_real"} onValueChange={setStrategy}>
            <SelectTrigger data-testid="select-strategy" className="h-11 bg-white border-[#E5E2DC]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed_real">Fixed real $ — same buying power yearly</SelectItem>
              <SelectItem value="percentage">% of portfolio — flexible to markets</SelectItem>
              <SelectItem value="guardrails">Guardrails — Guyton-Klinger rules</SelectItem>
            </SelectContent>
          </Select>
          {inputs.withdrawal_strategy === "percentage" && (
            <NumInput
              id="withdraw-pct"
              testid="input-withdrawal-pct"
              label="Withdrawal %"
              value={inputs.withdrawal_percentage ?? 4}
              onChange={(v) => setInputs({ ...inputs, withdrawal_percentage: v })}
              suffix="%"
              step={0.1}
            />
          )}
          {inputs.withdrawal_strategy === "guardrails" && (
            <div className="grid grid-cols-2 gap-3">
              <NumInput id="g-initial" testid="input-g-initial" label="Initial rate" value={inputs.guardrails?.initial_pct ?? 4} onChange={(v) => updateGuardrail("initial_pct", v)} suffix="%" step={0.1} />
              <NumInput id="g-upper" testid="input-g-upper" label="Upper band" value={inputs.guardrails?.upper_band ?? 1.2} onChange={(v) => updateGuardrail("upper_band", v)} suffix="x" step={0.05} />
              <NumInput id="g-lower" testid="input-g-lower" label="Lower band" value={inputs.guardrails?.lower_band ?? 0.8} onChange={(v) => updateGuardrail("lower_band", v)} suffix="x" step={0.05} />
              <NumInput id="g-raise" testid="input-g-raise" label="Raise" value={inputs.guardrails?.raise_pct ?? 10} onChange={(v) => updateGuardrail("raise_pct", v)} suffix="%" step={1} />
              <NumInput id="g-cut" testid="input-g-cut" label="Cut" value={inputs.guardrails?.cut_pct ?? 10} onChange={(v) => updateGuardrail("cut_pct", v)} suffix="%" step={1} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
